import React, { useState, useEffect, useRef, useCallback } from "react";
import { getAuthService } from "../../services/factory";
import { requestManager } from "../../utils/requestManager";
import { VerificationMethod } from "../../services/authService";
import useOtpTimer from "../../hooks/useOtpTimer";
import Input from "../ui/Input";
import FormGroup from "../ui/FormGroup";
import checkCircle from "../../img/check-circle.svg";
import informationCircle from "../../img/information-circle.svg";
import { isValidMaskedPhone } from "../../utils/phoneMask";
import { validateMaskedPhone } from "../../utils/validation";
import FormError from "../ui/FormError";

interface PhoneVerificationInlineProps {
	phone: string;
	setPhone: (phone: string) => void;
	phoneVerified: boolean;
	setPhoneVerified: (verified: boolean) => void;
	disabled?: boolean;
	error?: string;
}

const PhoneVerificationInline: React.FC<PhoneVerificationInlineProps> = ({ phone, setPhone, phoneVerified, setPhoneVerified, disabled = false, error = "" }) => {
	const [phoneVerificationSent, setPhoneVerificationSent] = useState(false);
	const [phoneVerificationCode, setPhoneVerificationCode] = useState("");
	const [isSendingVerification, setIsSendingVerification] = useState(false);
	const [otpError, setOtpError] = useState<string | null>(null);
	const [verificationMethod, setVerificationMethod] = useState<VerificationMethod>("mobile_id");
	const [smsSentCount, setSmsSentCount] = useState(0);
	const [mobileIdReqId, setMobileIdReqId] = useState<string | null>(null);
	const sentRef = useRef(false);
	const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const { timeLeft, startTimer, resetTimer, formatTime } = useOtpTimer();

	const getSmsFallbackTimeout = (): number => {
		if (typeof window !== "undefined" && (window as any).SkAuthConfig?.smsFallbackTimeoutSeconds) {
			const val = parseInt((window as any).SkAuthConfig.smsFallbackTimeoutSeconds, 10);
			if (!isNaN(val) && val > 0) {
				return val;
			}
		}
		return parseInt(import.meta.env.VITE_SMS_FALLBACK_TIMEOUT || "60", 10);
	};
	const SMS_FALLBACK_TIMEOUT = getSmsFallbackTimeout();

	const getOtpSmsLength = (): number => {
		if (typeof window !== "undefined" && (window as any).SkAuthConfig?.otpSmsLength) {
			const val = parseInt((window as any).SkAuthConfig.otpSmsLength, 10);
			if (!isNaN(val) && val >= 4 && val <= 10) {
				return val;
			}
		}
		return parseInt(import.meta.env.VITE_OTP_SMS_LENGTH || "6", 10);
	};
	const OTP_SMS_LENGTH = getOtpSmsLength();

	const isFlashcallEnabled = (): boolean => {
		if (typeof window !== "undefined" && (window as any).SkAuthConfig?.flashcallEnabled !== undefined) {
			return !!(window as any).SkAuthConfig.flashcallEnabled;
		}
		return true;
	};
	const FLASHCALL_ENABLED = isFlashcallEnabled();
	const SMS_MAX_COUNT = 4;

	const authService = getAuthService();

	const stopMobileIdPolling = useCallback(() => {
		if (pollingIntervalRef.current) {
			clearInterval(pollingIntervalRef.current);
			pollingIntervalRef.current = null;
		}
	}, []);

	const sendVerification = async (method: VerificationMethod, force = false) => {
		const trimmedPhone = phone.trim();
		if (!trimmedPhone) {
			setOtpError("Введите номер телефона");
			return;
		}

		const phoneValidation = validateMaskedPhone(trimmedPhone);
		if (!phoneValidation.valid) {
			setOtpError(phoneValidation.error || "Введите корректный номер телефона");
			return;
		}

		const key = `sendVerification:${trimmedPhone}:${method}`;
		if (!force && requestManager.isRequestInProgress(key)) {
			return;
		}
		if (method === "sms" && smsSentCount >= SMS_MAX_COUNT) {
			setOtpError("Превышено максимальное количество SMS");
			return;
		}

		stopMobileIdPolling();
		setIsSendingVerification(true);
		setOtpError(null);

		try {
			if (force) {
				requestManager.cancelRequest(key);
			}
			const result = await authService.sendVerification(trimmedPhone, {
				method,
				force,
			});

			if (result.success) {
				setPhoneVerificationSent(true);
				sentRef.current = true;
				setVerificationMethod(method);

				if (method === "mobile_id" && result.authReqId) {
					setMobileIdReqId(result.authReqId);
					startTimer(15);
				} else {
					if (method === "sms") {
						setSmsSentCount((prev) => prev + 1);
					}
					startTimer(SMS_FALLBACK_TIMEOUT);
				}
			} else {
				if (method === "mobile_id") {
					const fallbackMethod = FLASHCALL_ENABLED ? "flashcall" : "sms";
					console.log(`Mobile ID not available, falling back to ${fallbackMethod}`);
					await sendVerification(fallbackMethod, true);
					return;
				}
				setOtpError(result.errors?.[0] || `Не удалось отправить ${method === "flashcall" ? "звонок" : "SMS"}`);
			}
		} catch {
			if (method === "mobile_id") {
				const fallbackMethod = FLASHCALL_ENABLED ? "flashcall" : "sms";
				await sendVerification(fallbackMethod, true);
				return;
			}
			setOtpError("Ошибка при отправке кода");
		} finally {
			setIsSendingVerification(false);
		}
	};

	// Mobile ID polling logic
	useEffect(() => {
		if (verificationMethod === "mobile_id" && mobileIdReqId && phoneVerificationSent && !phoneVerified) {
			pollingIntervalRef.current = setInterval(async () => {
				try {
					const res = await authService.checkMobileIdStatus(mobileIdReqId);
					if (res.success && res.status === "success") {
						stopMobileIdPolling();
						setPhoneVerified(true);
						setOtpError(null);
					} else if (res.status === "awaiting_sms_otp") {
						setVerificationMethod("sms");
						setOtpError(null);
						startTimer(SMS_FALLBACK_TIMEOUT);
					} else if (res.status === "failed" || res.status === "expired") {
						stopMobileIdPolling();
						const fallbackMethod = FLASHCALL_ENABLED ? "flashcall" : "sms";
						sendVerification(fallbackMethod, true);
					}
				} catch {
					// Ignore transient polling errors
				}
			}, 2000);
		}

		return () => {
			stopMobileIdPolling();
		};
	}, [verificationMethod, mobileIdReqId, phoneVerificationSent, phoneVerified, authService, setPhoneVerified, stopMobileIdPolling, SMS_FALLBACK_TIMEOUT, startTimer, FLASHCALL_ENABLED]);

	// Reset verification state when phone changes
	useEffect(() => {
		if (sentRef.current) {
			stopMobileIdPolling();
			sentRef.current = false;
			setPhoneVerificationSent(false);
			setPhoneVerificationCode("");
			setOtpError(null);
			setVerificationMethod("mobile_id");
			setSmsSentCount(0);
			setMobileIdReqId(null);
			resetTimer();
		}
	}, [phone, resetTimer, stopMobileIdPolling]);

	const handleStartVerification = async () => {
		setOtpError(null);

		const trimmedPhone = phone.trim();
		if (!trimmedPhone) {
			setOtpError("Введите номер телефона");
			return;
		}

		setIsSendingVerification(true);
		try {
			const check = await authService.checkIdentifier(trimmedPhone);
			if (check.exists) {
				setOtpError("Пользователь с таким номером уже зарегистрирован");
				return;
			}
		} catch {
			// If check fails, proceed with verification
		} finally {
			setIsSendingVerification(false);
		}

		sendVerification("mobile_id");
	};

	const handleSwitchToFlashcall = () => {
		stopMobileIdPolling();
		sendVerification("flashcall", true);
	};

	const handleSwitchToSms = () => {
		stopMobileIdPolling();
		sendVerification("sms", true);
	};

	const expectedLength = verificationMethod === "flashcall" ? 4 : OTP_SMS_LENGTH;

	const handleVerifyPhoneCode = useCallback(async () => {
		const trimmedCode = phoneVerificationCode.trim();
		if (!trimmedCode) return;

		try {
			stopMobileIdPolling();
			const result = await authService.verifyOtp(phone.trim(), trimmedCode);

			if (result.success) {
				setPhoneVerified(true);
				setOtpError(null);
			} else {
				setOtpError(result.errors?.[0] || "Неверный код");
			}
		} catch {
			setOtpError("Ошибка при проверке кода");
		}
	}, [phoneVerificationCode, authService, phone, setPhoneVerified, stopMobileIdPolling]);

	// Auto-submit code when enough digits are entered
	useEffect(() => {
		const trimmedCode = phoneVerificationCode.trim();
		if (trimmedCode.length === expectedLength && !disabled && !phoneVerified) {
			handleVerifyPhoneCode();
		}
	}, [phoneVerificationCode, expectedLength, disabled, phoneVerified, handleVerifyPhoneCode]);

	if (phoneVerified) {
		return (
			<FormGroup>
				<div className="auth-field">
					<label htmlFor="identifier-input" className="auth-field__label">
						Телефон
					</label>

					<Input id="phone-verified" type="tel" value={phone} readOnly placeholder="Телефон" required disabled={disabled} className="auth-input-disabled" />
				</div>
				<div className="auth-check">
					<img src={checkCircle} alt="" className="auth-check__icon" />
					<span>Подтверждён</span>
				</div>
			</FormGroup>
		);
	}

	const hasValue = (value: string) => value.trim().length > 0;
	const showPhoneLabel = hasValue(phone);
	const showCodeLabel = hasValue(phoneVerificationCode);

	const isPhoneValid = phone.trim() && isValidMaskedPhone(phone.trim());
	const isSendButtonDisabled = disabled || isSendingVerification || !isPhoneValid;
	const smsRemaining = SMS_MAX_COUNT - smsSentCount;

	return (
		<FormGroup error={otpError || undefined}>
			<div className="tw-flex tw-flex-col tw-gap-3">
				{/* Phone input + send code button */}
				<div className="auth-sms-verification">
					<div className="auth-field">
						{showPhoneLabel && (
							<label htmlFor="phone-input" className="auth-field__label">
								Телефон
							</label>
						)}
						<Input id="phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Телефон" required disabled={disabled || phoneVerificationSent} clearable onClear={() => setPhone("")} />
					</div>

					{/* Code input */}
					{phoneVerificationSent && verificationMethod !== "mobile_id" && (
						<div className="auth-field">
							{showCodeLabel && (
								<label htmlFor="phone-code-input" className="auth-field__label">
									{verificationMethod === "flashcall" ? "Последние 4 цифры" : "Код из SMS"}
								</label>
							)}
							<Input
								id="phone-code-input"
								type="text"
								inputMode="numeric"
								pattern="[0-9]*"
								maxLength={expectedLength}
								value={phoneVerificationCode}
								onChange={(e) => {
									setPhoneVerificationCode(e.target.value);
									setOtpError(null);
								}}
								placeholder={verificationMethod === "flashcall" ? "Последние 4 цифры" : "Код из SMS"}
								disabled={disabled}
								autoFocus
								clearable
								onClear={() => setPhoneVerificationCode("")}
							/>
						</div>
					)}

					{!phoneVerificationSent && (
						<button onClick={handleStartVerification} disabled={isSendButtonDisabled} className={"auth-button-sms " + (isSendButtonDisabled ? "disabled" : "")}>
							{isSendingVerification ? "Отправка..." : "Получить код"}
						</button>
					)}
				</div>

				{/* Mobile ID banner */}
				{phoneVerificationSent && verificationMethod === "mobile_id" && (
					<div className="auth-mobile-id-banner">
						<div className="auth-mobile-id-spinner" />
						<div className="auth-mobile-id-text">
							<p className="auth-mobile-id-title">Ожидаем подтверждения на телефоне</p>
							<p className="auth-mobile-id-subtitle">Нажмите «Принять» во всплывающем окне на вашем смартфоне</p>
						</div>
					</div>
				)}

				{/* Hint text with icon & fallback timer chain */}
				{phoneVerificationSent && (
					<div className="auth-verify-hint">
						<img src={informationCircle} alt="" className="auth-verify-hint__icon" />
						<div className="tw-flex tw-flex-col tw-gap-1">
							<span className="auth-verify-hint__text">
								{verificationMethod === "mobile_id"
									? "Подтвердите вход на вашем телефоне (Мобильный ID)"
									: verificationMethod === "flashcall"
									? "Уже звоним. Введите последние 4 цифры входящего номера"
									: "Введите код из SMS"}
							</span>

							{verificationMethod === "mobile_id" ? (
								FLASHCALL_ENABLED ? (
									timeLeft > 0 ? (
										<p className="auth-recive-sms-button">Запросить звонок-сброс через {formatTime(timeLeft)}</p>
									) : (
										<div className="auth-recive-sms-timeout-box">
											<button type="button" className="auth-recive-sms-timeout" onClick={handleSwitchToFlashcall} disabled={isSendingVerification}>
												Получить звонок-сброс
											</button>
										</div>
									)
								) : (
									timeLeft > 0 ? (
										<p className="auth-recive-sms-button">Получить код по SMS через {formatTime(timeLeft)}</p>
									) : (
										<div className="auth-recive-sms-timeout-box">
											<button type="button" className={"auth-recive-sms-timeout " + (isSendingVerification || smsRemaining <= 0 ? "disabled" : "")} onClick={handleSwitchToSms} disabled={isSendingVerification || smsRemaining <= 0}>
												Получить код по SMS
											</button>
										</div>
									)
								)
							) : verificationMethod === "flashcall" ? (
								timeLeft > 0 ? (
									<p className="auth-recive-sms-button">Получить код по SMS через {formatTime(timeLeft)}</p>
								) : (
									<div className="auth-recive-sms-timeout-box">
										<button type="button" className={"auth-recive-sms-timeout " + (isSendingVerification || smsRemaining <= 0 ? "disabled" : "")} onClick={handleSwitchToSms} disabled={isSendingVerification || smsRemaining <= 0}>
											Получить код по SMS
										</button>
									</div>
								)
							) : (
								timeLeft > 0 ? (
									<p className="auth-recive-sms-button">Отправить SMS повторно через {formatTime(timeLeft)}</p>
								) : (
									<div className="auth-recive-sms-timeout-box">
										<button type="button" className={"auth-recive-sms-timeout " + (isSendingVerification || smsRemaining <= 0 ? "disabled" : "")} onClick={handleSwitchToSms} disabled={isSendingVerification || smsRemaining <= 0}>
											Отправить SMS повторно
										</button>
									</div>
								)
							)}
						</div>
					</div>
				)}
			</div>
			<FormError message={error} />
		</FormGroup>
	);
};

export default PhoneVerificationInline;
